import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { 
  Map as MapIcon, 
  LayoutDashboard, 
  Users, 
  Target, 
  ZoomIn, 
  ZoomOut, 
  X, 
  Hexagon,
  Move,
  ChevronLeft,
  ChevronRight,
  Maximize,
  AlertCircle,
  Activity,
  Compass,
  Info,
  CheckSquare,
  Rocket,
  Zap,
  Gamepad2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import ActivityRTSOverlay from "@/components/civilization/ActivityRTSOverlay";
import NodeSelector from "@/components/civilization/NodeSelector";

// --- Utility Functions for Hex Grid ---

function getHexCorner(center, size, i) {
    const angle_deg = 60 * i - 30;
    const angle_rad = Math.PI / 180 * angle_deg;
    return {
        x: center.x + size * Math.cos(angle_rad),
        y: center.y + size * Math.sin(angle_rad)
    };
}

function HexagonShape({ x, y, size, selected, onClick, children, className }) {
    const points = [];
    for (let i = 0; i < 6; i++) {
        points.push(getHexCorner({x, y}, size, i));
    }
    const pointsStr = points.map(p => `${p.x},${p.y}`).join(" ");

    return (
        <g 
            className={`transition-all duration-300 ${className}`} 
            onClick={onClick}
        >
            <polygon 
                points={pointsStr} 
                // className handles stroke/fill
                strokeWidth={selected ? 3 : 1}
            />
            {/* Glow effect for selected */}
            {selected && (
                 <polygon 
                 points={pointsStr} 
                 className="fill-none stroke-white blur-sm opacity-50"
                 strokeWidth={4}
             />
            )}
            
            <foreignObject x={x - size} y={y - size} width={size * 2} height={size * 2} className="pointer-events-none">
                <div className="w-full h-full flex items-center justify-center p-2 text-center">
                    {children}
                </div>
            </foreignObject>
        </g>
    );
}

function generateHexLayout(items) {
    if (items.length === 0) return [];

    // Generate grid coordinates: left-to-right, top-to-bottom
    // This creates a rectangular grid pattern for hex layout
    const COLS_PER_ROW = 5; // Number of hexes per row
    
    return items.map((item, index) => {
        const row = Math.floor(index / COLS_PER_ROW);
        const col = index % COLS_PER_ROW;
        
        // For pointy-top hexagons in offset coordinates:
        // Even rows: col maps directly to q
        // Odd rows: offset by 0.5 (handled in pixel conversion)
        // r is the row
        return {
            item,
            q: col,
            r: row,
            isOddRow: row % 2 === 1
        };
    });
}

function hexToPixel(q, r, size, isOddRow = false) {
    // Offset coordinate system for grid layout
    const horizontalSpacing = size * Math.sqrt(3);
    const verticalSpacing = size * 1.5;
    
    // Offset odd rows by half a hex width
    const xOffset = isOddRow ? horizontalSpacing / 2 : 0;
    
    const x = q * horizontalSpacing + xOffset;
    const y = r * verticalSpacing;
    return { x, y };
}

export default function CivilizationMap() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [transitionDuration, setTransitionDuration] = useState(0); // For camera animations
  const [isExiting, setIsExiting] = useState(false); // For drill-up animations
  
  // Navigation State
  const [viewLevel, setViewLevel] = useState('mission'); // 'mission', 'objective', 'activity'
  const [parentId, setParentId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]); // Array of { level, id, title }
  
  // Grouping State
  const [groupingMode, setGroupingMode] = useState('default'); // 'default', 'by_owner', 'custom'
  const [archiveFilter, setArchiveFilter] = useState('active'); // 'all', 'active', 'archived'
  const [customPositions, setCustomPositions] = useState({}); // { itemId: { x, y } }
  const [draggingHex, setDraggingHex] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const [selectedNode, setSelectedNode] = useState(null); // The clicked node (for sidebar)
  const [selectedRTSActivity, setSelectedRTSActivity] = useState(null);
  const [activeHexId, setActiveHexId] = useState(null); // The hex currently showing controls
  const [isMobileRightSidebarOpen, setIsMobileRightSidebarOpen] = useState(false);
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false);
  
  const mapRef = useRef(null);
  const lastPinchDist = useRef(null);
  const HEX_SIZE = 80; // Slightly larger for more content

  // Data fetching
  const { data: missions = [] } = useQuery({
    queryKey: ['missions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  const { data: officers = [] } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });

  const { data: objectives = [] } = useQuery({
    queryKey: ['objectives'],
    queryFn: () => base44.entities.Objective.list(),
    initialData: []
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['activities'],
    queryFn: () => base44.entities.Activity.list(),
    initialData: []
  });

  const { data: issues = [] } = useQuery({
    queryKey: ['issues'],
    queryFn: () => base44.entities.Issue.list(),
    initialData: []
  });

  const { data: dailyTasks = [] } = useQuery({
    queryKey: ['dailyTasks'],
    queryFn: () => base44.entities.DailyTask.list(),
    initialData: []
  });

  // Helper to determine visual status
  const getVisualStatus = (item, type) => {
    // Status mapping: Active (Green), Contested (Yellow/Amber), Scout (Blue), Conquered (Gold), Abandoned (Gray), Critical (Red)
    const status = item.status;
    const flag = item.status_flag; // All entities now use status_flag consistently
    
    // For archived items, always show abandoned
    if (status === 'archived') return 'abandoned';
    
    // For active items, use the status_flag color directly
    if (flag === 'red') return 'critical';
    if (flag === 'green') return 'active';
    // Default to contested (amber/yellow) if no flag or yellow flag
    return 'contested';
  };

  const getHexStyles = (status) => {
      switch(status) {
          case 'active': return 'stroke-emerald-500 fill-emerald-500/10 hover:fill-emerald-500/20';
          case 'critical': return 'stroke-rose-500 fill-rose-500/10 hover:fill-rose-500/20';
          case 'contested': return 'stroke-amber-500 fill-amber-500/10 hover:fill-amber-500/20';
          case 'scout': return 'stroke-cyan-500 fill-cyan-500/10 hover:fill-cyan-500/20';
          case 'conquered': return 'stroke-yellow-400 fill-yellow-400/10 hover:fill-yellow-400/20';
          case 'abandoned': return 'stroke-slate-500 fill-slate-500/10 hover:fill-slate-500/20';
          default: return 'stroke-amber-500 fill-amber-500/10 hover:fill-amber-500/20';
      }
  };

  const getStatusGlow = (status) => {
      switch(status) {
          case 'active': return 'stroke-emerald-400';
          case 'critical': return 'stroke-rose-400';
          case 'contested': return 'stroke-amber-400';
          case 'scout': return 'stroke-cyan-400';
          case 'conquered': return 'stroke-yellow-300';
          case 'abandoned': return 'stroke-slate-500';
          default: return 'stroke-slate-400';
      }
  };

  // Helper to calculate status breakdown
  const getStatusBreakdown = (items, type) => {
      const breakdown = { green: 0, yellow: 0, red: 0, total: items.length };
      items.forEach(item => {
          const flag = item.status_flag || item.key_results_status || 'yellow'; // default yellow
          if (flag === 'green') breakdown.green++;
          else if (flag === 'red') breakdown.red++;
          else breakdown.yellow++;
      });
      return breakdown;
  };

  // Prepare items for the current view level
  const currentItems = useMemo(() => {
    let items = [];
    if (viewLevel === 'mission') {
        let filteredMissions = missions;
        if (archiveFilter === 'active') filteredMissions = missions.filter(m => m.status !== 'archived');
        if (archiveFilter === 'archived') filteredMissions = missions.filter(m => m.status === 'archived');
        
        items = filteredMissions.map(m => {
            const mObjectives = objectives.filter(o => o.mission_id === m.id);
            const mActivities = activities.filter(a => mObjectives.some(o => o.id === a.objective_id));
            const mTasks = dailyTasks.filter(t => mActivities.some(a => a.id === t.activity_id));

            return {
                ...m,
                nodeType: 'mission',
                code: m.mission_code,
                visualStatus: getVisualStatus(m, 'mission'),
                stats: {
                    objectives: getStatusBreakdown(mObjectives, 'objective'),
                    activities: getStatusBreakdown(mActivities, 'activity'),
                    tasks: getStatusBreakdown(mTasks, 'task')
                },
                officers: [m.owner_email, m.sponsor_email].filter(Boolean),
                issueCount: 0 // Simplified
            };
        });
    } else if (viewLevel === 'objective') {
        let filteredObjectives = objectives.filter(o => o.mission_id === parentId);
        if (archiveFilter === 'active') filteredObjectives = filteredObjectives.filter(o => o.status !== 'archived');
        if (archiveFilter === 'archived') filteredObjectives = filteredObjectives.filter(o => o.status === 'archived');
        
        items = filteredObjectives.map(o => {
            const oActivities = activities.filter(a => a.objective_id === o.id);
            const oTasks = dailyTasks.filter(t => oActivities.some(a => a.id === t.activity_id));
            
            return {
                ...o,
                title: o.statement, // Use statement as title
                code: o.objective_code,
                nodeType: 'objective',
                visualStatus: getVisualStatus(o, 'objective'),
                stats: {
                    activities: getStatusBreakdown(oActivities, 'activity'),
                    tasks: getStatusBreakdown(oTasks, 'task')
                },
                officers: [o.owner_email].filter(Boolean),
                issueCount: issues.filter(i => i.linked_objective_id === o.id).length
            };
        });
    } else if (viewLevel === 'activity') {
        let filteredActivities = activities.filter(a => a.objective_id === parentId);
        if (archiveFilter === 'active') filteredActivities = filteredActivities.filter(a => a.status !== 'archived');
        if (archiveFilter === 'archived') filteredActivities = filteredActivities.filter(a => a.status === 'archived');
        
        items = filteredActivities.map(a => {
            const aTasks = dailyTasks.filter(t => t.activity_id === a.id);
            
            return {
                ...a,
                nodeType: 'activity',
                code: a.activity_code,
                visualStatus: getVisualStatus(a, 'activity'),
                stats: {
                    tasks: getStatusBreakdown(aTasks, 'task')
                },
                officers: [a.responsible_email, a.accountable_email].filter(Boolean),
                issueCount: issues.filter(i => i.linked_activity_id === a.id).length
            };
        });
    }
    return items;
  }, [viewLevel, parentId, missions, objectives, activities, issues, dailyTasks, archiveFilter]);

  // Get officer profile by email
  const getOfficerProfile = (email) => officers.find(o => o.user_email === email) || { callsign: email?.split('@')[0] || 'Unknown', rank_title: 'Officer', clearance_level: 1 };

  // Drill navigation handlers for NodeSelector
  const handleDrillToObjective = useCallback((objectiveId, missionId) => {
    // First navigate to objectives view for the mission
    setTransitionDuration(0.8);
    const containerW = mapRef.current?.clientWidth || window.innerWidth;
    const containerH = mapRef.current?.clientHeight || window.innerHeight;
    
    // Navigate to mission's objectives first
    setBreadcrumbs([{ level: 'mission', id: null, title: 'Missions' }]);
    setParentId(missionId);
    setViewLevel('objective');
    
    // After transition, highlight the objective
    setTimeout(() => {
      setTransitionDuration(0);
      setZoom(1);
      setPan({ x: containerW / 2, y: containerH / 2 });
      
      // Find and highlight the objective
      setTimeout(() => {
        setActiveHexId(objectiveId);
        const foundObj = objectives.find(o => o.id === objectiveId);
        if (foundObj) {
          setSelectedNode({
            ...foundObj,
            title: foundObj.statement,
            code: foundObj.objective_code,
            nodeType: 'objective',
            visualStatus: foundObj.status_flag === 'red' ? 'critical' : foundObj.status_flag === 'green' ? 'active' : 'contested',
            officers: [foundObj.owner_email].filter(Boolean)
          });
        }
      }, 100);
    }, 800);
  }, [objectives]);

  const handleDrillToActivity = useCallback((activityId, objectiveId, missionId) => {
    setTransitionDuration(0.8);
    const containerW = mapRef.current?.clientWidth || window.innerWidth;
    const containerH = mapRef.current?.clientHeight || window.innerHeight;
    
    // Navigate to objective's activities
    setBreadcrumbs([
      { level: 'mission', id: null, title: 'Missions' },
      { level: 'objective', id: missionId, title: 'Objectives' }
    ]);
    setParentId(objectiveId);
    setViewLevel('activity');
    
    // After transition, open the activity overlay
    setTimeout(() => {
      setTransitionDuration(0);
      setZoom(1);
      setPan({ x: containerW / 2, y: containerH / 2 });
      
      // Find and open the activity
      setTimeout(() => {
        const foundActivity = activities.find(a => a.id === activityId);
        if (foundActivity) {
          setSelectedRTSActivity(foundActivity);
          setActiveHexId(activityId);
        }
      }, 100);
    }, 800);
  }, [activities]);

  const activeMissionsCount = missions.filter(m => m.status === 'active').length;
  const activeObjectivesCount = objectives.filter(o => o.status === 'active').length;
  const openIssuesCount = issues.filter(i => i.status === 'open' || i.status === 'under_review').length;
  const totalOfficersCount = officers.length;

  // Handle search param from URL (e.g., from issue card links)
  useEffect(() => {
    const searchCode = searchParams.get('search');
    if (!searchCode || missions.length === 0) return;

    // Clear the search param to avoid re-triggering
    setSearchParams({}, { replace: true });

    // Find matching entity by code
    const mission = missions.find(m => m.mission_code === searchCode);
    if (mission) {
      setActiveHexId(mission.id);
      setSelectedNode({
        ...mission,
        nodeType: 'mission',
        code: mission.mission_code,
        visualStatus: mission.status_flag === 'red' ? 'critical' : mission.status_flag === 'green' ? 'active' : 'contested',
        officers: [mission.owner_email, mission.sponsor_email].filter(Boolean)
      });
      return;
    }

    const objective = objectives.find(o => o.objective_code === searchCode);
    if (objective) {
      handleDrillToObjective(objective.id, objective.mission_id);
      return;
    }

    const activity = activities.find(a => a.activity_code === searchCode);
    if (activity) {
      const obj = objectives.find(o => o.id === activity.objective_id);
      handleDrillToActivity(activity.id, activity.objective_id, obj?.mission_id);
      return;
    }
  }, [searchParams, missions, objectives, activities]);

  // State for transition animation
  const [isGroupTransitioning, setIsGroupTransitioning] = useState(false);
  const prevGroupingMode = useRef(groupingMode);

  // Handle grouping mode change with animation
  useEffect(() => {
    if (prevGroupingMode.current !== groupingMode) {
      setIsGroupTransitioning(true);
      const timeout = setTimeout(() => setIsGroupTransitioning(false), 600);
      prevGroupingMode.current = groupingMode;
      return () => clearTimeout(timeout);
    }
  }, [groupingMode]);

  // Generate hex layout based on grouping mode
  const hexLayoutData = useMemo(() => {
    if (groupingMode === 'custom') {
      // Custom: Use stored positions, fallback to default layout for items without custom positions
      const defaultLayout = generateHexLayout(currentItems);
      const hexLayout = defaultLayout.map((node, index) => {
        if (customPositions[node.item.id]) {
          return { ...node, customX: customPositions[node.item.id].x, customY: customPositions[node.item.id].y };
        }
        return node;
      });
      return { hexLayout, clusterLabels: [] };
    } else if (groupingMode === 'default') {
      // XP Ranked: Sort by XP descending, then arrange left-to-right, top-to-bottom
      const sortedItems = [...currentItems].sort((a, b) => (b.xp_reward || 0) - (a.xp_reward || 0));
      return { hexLayout: generateHexLayout(sortedItems), clusterLabels: [] };
    } else if (groupingMode === 'by_owner') {
      // Group by owner_email
      const groups = {};
      currentItems.forEach(item => {
        const owner = item.owner_email || item.sponsor_email || 'unassigned';
        if (!groups[owner]) groups[owner] = [];
        groups[owner].push(item);
      });

      const allHexes = [];
      const clusterLabels = [];
      const ownerKeys = Object.keys(groups);
      
      // Calculate the bounding box for each cluster to determine spacing
      const getClusterBounds = (itemCount) => {
        const layout = generateHexLayout(Array(itemCount).fill({}));
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        layout.forEach(node => {
          const { x, y } = hexToPixel(node.q, node.r, HEX_SIZE, node.isOddRow);
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        });
        return { 
          width: maxX - minX + HEX_SIZE * 2.5, 
          height: maxY - minY + HEX_SIZE * 2.5 
        };
      };

      // Find max cluster dimensions for consistent spacing
      let maxClusterWidth = 0;
      let maxClusterHeight = 0;
      ownerKeys.forEach(owner => {
        const bounds = getClusterBounds(groups[owner].length);
        maxClusterWidth = Math.max(maxClusterWidth, bounds.width);
        maxClusterHeight = Math.max(maxClusterHeight, bounds.height);
      });

      const clustersPerRow = Math.max(2, Math.ceil(Math.sqrt(ownerKeys.length)));
      const CLUSTER_SPACING_X = maxClusterWidth + 80; // Add padding between clusters
      const CLUSTER_SPACING_Y = maxClusterHeight + 100; // Add padding + room for label

      ownerKeys.forEach((owner, clusterIndex) => {
        const clusterRow = Math.floor(clusterIndex / clustersPerRow);
        const clusterCol = clusterIndex % clustersPerRow;
        const clusterBaseX = (clusterCol - (clustersPerRow - 1) / 2) * CLUSTER_SPACING_X;
        const clusterBaseY = clusterRow * CLUSTER_SPACING_Y;

        const officer = getOfficerProfile(owner);
        clusterLabels.push({
          id: `label-${owner}`,
          x: clusterBaseX,
          y: clusterBaseY - 60,
          callsign: officer.callsign || owner,
          rank: officer.rank_title || 'Officer',
          level: officer.clearance_level || 1,
          avatar: officer.avatar_url,
        });

        const clusterItems = groups[owner];
        const clusterLayout = generateHexLayout(clusterItems);

        clusterLayout.forEach((node) => {
          allHexes.push({
            ...node,
            clusterOffsetX: clusterBaseX,
            clusterOffsetY: clusterBaseY,
            cluster: owner,
          });
        });
      });

      return { hexLayout: allHexes, clusterLabels };
    }
    return { hexLayout: generateHexLayout(currentItems), clusterLabels: [] };
  }, [currentItems, groupingMode, officers, customPositions]);

  const hexLayout = hexLayoutData.hexLayout;
  const clusterLabels = hexLayoutData.clusterLabels;

  const fitBounds = () => {
      if (!mapRef.current || hexLayout.length === 0) {
        if(mapRef.current) setPan({ x: mapRef.current.clientWidth / 2, y: mapRef.current.clientHeight / 2 });
        setZoom(1);
        return;
      }

      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      
      hexLayout.forEach(node => {
          const { x, y } = hexToPixel(node.q, node.r, HEX_SIZE, node.isOddRow);
          minX = Math.min(minX, x - HEX_SIZE);
          maxX = Math.max(maxX, x + HEX_SIZE);
          minY = Math.min(minY, y - HEX_SIZE);
          maxY = Math.max(maxY, y + HEX_SIZE);
      });

      const padding = 100;
      const width = maxX - minX + padding * 2;
      const height = maxY - minY + padding * 2;
      
      const containerWidth = mapRef.current.clientWidth;
      const containerHeight = mapRef.current.clientHeight;

      const scaleX = containerWidth / width;
      const scaleY = containerHeight / height;
      const newZoom = Math.min(Math.max(Math.min(scaleX, scaleY), 0.2), 1.5); // Cap zoom

      // Center of bounding box
      const midX = (minX + maxX) / 2;
      const midY = (minY + maxY) / 2;

      // Center of screen
      const screenMidX = containerWidth / 2;
      const screenMidY = containerHeight / 2;

      // Calculate pan to center the bounding box mid at screen mid
      // screenMid = pan + mid * zoom
      // pan = screenMid - mid * zoom
      const newPanX = screenMidX - midX * newZoom;
      const newPanY = screenMidY - midY * newZoom;

      setZoom(newZoom);
      setPan({ x: newPanX, y: newPanY });
  };

  // Center map initially when data loads
  useEffect(() => {
     if(mapRef.current && hexLayout.length > 0) {
         fitBounds();
     } else if(mapRef.current) {
         setPan({ x: mapRef.current.clientWidth / 2, y: mapRef.current.clientHeight / 2 });
     }
  }, [hexLayout.length]);

  // Wheel zoom — must use non-passive listener so preventDefault works
  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const handleWheel = (e) => {
      e.preventDefault();
      setTransitionDuration(0);
      const scaleAmount = -e.deltaY * 0.001;
      setZoom(z => Math.min(Math.max(0.2, z + scaleAmount), 3));
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  const handleMouseDown = (e) => {
      if (e.button === 0) {
          setTransitionDuration(0);
          setIsDragging(true);
          setLastMousePos({ x: e.clientX, y: e.clientY });
          // If clicking background (not a hex), clear active hex
          // Note: Bubbling from hex click is stopped, so this only fires on background
          setActiveHexId(null);
      }
  };

  const handleMouseMove = (e) => {
      if (draggingHex) {
          // Dragging a hex - update its position
          setTransitionDuration(0);
          const rect = mapRef.current.getBoundingClientRect();
          const mouseX = (e.clientX - rect.left - pan.x) / zoom;
          const mouseY = (e.clientY - rect.top - pan.y) / zoom;
          
          setCustomPositions(prev => ({
            ...prev,
            [draggingHex]: { 
              x: mouseX - dragOffset.x, 
              y: mouseY - dragOffset.y 
            }
          }));
      } else if (isDragging) {
          setTransitionDuration(0);
          const dx = e.clientX - lastMousePos.x;
          const dy = e.clientY - lastMousePos.y;
          setPan(p => ({ x: p.x + dx, y: p.y + dy }));
          setLastMousePos({ x: e.clientX, y: e.clientY });
      }
  };

  const handleMouseUp = () => {
      if (draggingHex) {
        setDraggingHex(null);
      }
      setIsDragging(false);
  };



  return (
    <div className="flex flex-col h-screen bg-slate-950 w-full font-serif overflow-hidden">

      <AnimatePresence>
        {selectedRTSActivity && (
          <ActivityRTSOverlay 
            activity={selectedRTSActivity} 
            onClose={() => setSelectedRTSActivity(null)}
            officers={officers}
          />
        )}
      </AnimatePresence>
      
      <div className="flex flex-1 overflow-hidden bg-slate-900 w-full relative">


        {/* --- MAIN MAP --- */}
        <main 
          className="flex-1 bg-slate-900 relative overflow-hidden w-full h-full"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-950/20 via-slate-950/80 to-slate-950 pointer-events-none" />

          {/* View Level Navigation Bar */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center gap-2 pointer-events-auto w-[calc(100%-2rem)] sm:w-auto max-w-full px-2 sm:px-0">
            {/* Navigation Tabs */}
            <div className="flex items-center bg-slate-950/90 border border-slate-800 rounded-xl shadow-2xl backdrop-blur-md overflow-hidden w-full sm:w-auto">
              {/* Missions Tab */}
              <button
                onClick={() => {
                  if (viewLevel !== 'mission') {
                    setIsExiting(true);
                    setTimeout(() => {
                      setViewLevel('mission');
                      setParentId(null);
                      setBreadcrumbs([]);
                      setIsExiting(false);
                      const containerW = mapRef.current?.clientWidth || window.innerWidth;
                      const containerH = mapRef.current?.clientHeight || window.innerHeight;
                      setTransitionDuration(0);
                      setZoom(1);
                      setPan({ x: containerW / 2, y: containerH / 2 });
                    }, 300);
                  }
                }}
                className={`flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-5 py-2 sm:py-3 transition-all flex-1 sm:flex-initial ${
                  viewLevel === 'mission' 
                    ? 'bg-amber-500/20 text-amber-400 border-b-2 border-amber-400' 
                    : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
                }`}
              >
                <Rocket className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-[10px] sm:text-sm font-bold uppercase tracking-wide hidden xs:inline sm:inline">Missions</span>
              </button>

              {/* Divider */}
              <div className="w-px h-6 sm:h-8 bg-slate-700" />

              {/* Objectives Tab */}
              <button
                onClick={() => {
                  if (viewLevel === 'activity' && parentId) {
                    setIsExiting(true);
                    setTimeout(() => {
                      const currentObj = objectives.find(o => o.id === parentId);
                      setViewLevel('objective');
                      setParentId(currentObj ? currentObj.mission_id : null);
                      setBreadcrumbs(prev => prev.slice(0, 1));
                      setIsExiting(false);
                      const containerW = mapRef.current?.clientWidth || window.innerWidth;
                      const containerH = mapRef.current?.clientHeight || window.innerHeight;
                      setTransitionDuration(0);
                      setZoom(1);
                      setPan({ x: containerW / 2, y: containerH / 2 });
                    }, 300);
                  }
                }}
                disabled={viewLevel === 'mission'}
                className={`flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-5 py-2 sm:py-3 transition-all flex-1 sm:flex-initial ${
                  viewLevel === 'objective' 
                    ? 'bg-indigo-500/20 text-indigo-400 border-b-2 border-indigo-400' 
                    : viewLevel === 'mission'
                    ? 'text-slate-600 cursor-not-allowed'
                    : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
                }`}
              >
                <Compass className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-[10px] sm:text-sm font-bold uppercase tracking-wide hidden xs:inline sm:inline">Objectives</span>
              </button>

              {/* Divider */}
              <div className="w-px h-6 sm:h-8 bg-slate-700" />

              {/* Activities Tab */}
              <button
                disabled={viewLevel !== 'activity'}
                className={`flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-5 py-2 sm:py-3 transition-all flex-1 sm:flex-initial ${
                  viewLevel === 'activity' 
                    ? 'bg-emerald-500/20 text-emerald-400 border-b-2 border-emerald-400' 
                    : 'text-slate-600 cursor-not-allowed'
                }`}
              >
                <Activity className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-[10px] sm:text-sm font-bold uppercase tracking-wide hidden xs:inline sm:inline">Activities</span>
              </button>
            </div>

            {/* Grouping Controls */}
            <div className="flex gap-1 sm:gap-2 bg-black/90 backdrop-blur-md p-1.5 sm:p-2 rounded-xl border-2 border-cyan-500/50 shadow-[0_0_20px_rgba(0,255,255,0.3)]">
              <Button
                size="sm"
                variant={groupingMode === 'default' ? "default" : "outline"}
                onClick={() => setGroupingMode('default')}
                className={`h-8 sm:h-10 text-[10px] sm:text-xs font-bold gap-1 sm:gap-2 px-2 sm:px-4 ${
                  groupingMode === 'default' 
                    ? "bg-amber-500 hover:bg-amber-400 text-black border-2 border-amber-300 shadow-[0_0_15px_rgba(251,191,36,0.5)]" 
                    : "bg-slate-800 border-2 border-slate-600 text-white hover:bg-slate-700 hover:border-amber-500"
                }`}
              >
                <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">XP Ranked</span>
                <span className="sm:hidden">XP</span>
              </Button>
              <Button
                size="sm"
                variant={groupingMode === 'by_owner' ? "default" : "outline"}
                onClick={() => setGroupingMode('by_owner')}
                className={`h-8 sm:h-10 text-[10px] sm:text-xs font-bold gap-1 sm:gap-2 px-2 sm:px-4 ${
                  groupingMode === 'by_owner' 
                    ? "bg-indigo-500 hover:bg-indigo-400 text-white border-2 border-indigo-300 shadow-[0_0_15px_rgba(99,102,241,0.5)]" 
                    : "bg-slate-800 border-2 border-slate-600 text-white hover:bg-slate-700 hover:border-indigo-500"
                }`}
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">By Owner</span>
                <span className="sm:hidden">Owner</span>
              </Button>
              <Button
                size="sm"
                variant={groupingMode === 'custom' ? "default" : "outline"}
                onClick={() => {
                  if (groupingMode !== 'custom') {
                    // Initialize custom positions from current layout
                    setCustomPositions(prev => {
                      const newPositions = { ...prev };
                      hexLayout.forEach(n => {
                        if (!newPositions[n.item.id]) {
                          let nx, ny;
                          if (n.customX !== undefined) {
                            nx = n.customX;
                            ny = n.customY;
                          } else {
                            const basePos = hexToPixel(n.q, n.r, HEX_SIZE);
                            nx = basePos.x + (n.clusterOffsetX || 0);
                            ny = basePos.y + (n.clusterOffsetY || 0);
                          }
                          newPositions[n.item.id] = { x: nx, y: ny };
                        }
                      });
                      return newPositions;
                    });
                    setGroupingMode('custom');
                  }
                }}
                className={`h-8 sm:h-10 text-[10px] sm:text-xs font-bold gap-1 sm:gap-2 px-2 sm:px-4 ${
                  groupingMode === 'custom' 
                    ? "bg-emerald-500 hover:bg-emerald-400 text-white border-2 border-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.5)]" 
                    : "bg-slate-800 border-2 border-slate-600 text-white hover:bg-slate-700 hover:border-emerald-500"
                }`}
              >
                <Move className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Custom</span>
              </Button>

              {/* Archive Filter Toggle - Distinct segmented control style */}
              <div className="flex items-center h-8 sm:h-10 bg-slate-900/80 rounded-lg border border-slate-700 p-0.5 ml-2">
                <button
                  onClick={() => setArchiveFilter('all')}
                  className={`h-full px-2 sm:px-3 rounded-md text-[10px] sm:text-xs font-medium transition-all ${
                    archiveFilter === 'all' 
                      ? 'bg-slate-600 text-white shadow-sm' 
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setArchiveFilter('active')}
                  className={`h-full px-2 sm:px-3 rounded-md text-[10px] sm:text-xs font-medium transition-all ${
                    archiveFilter === 'active' 
                      ? 'bg-emerald-600 text-white shadow-sm' 
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setArchiveFilter('archived')}
                  className={`h-full px-2 sm:px-3 rounded-md text-[10px] sm:text-xs font-medium transition-all ${
                    archiveFilter === 'archived' 
                      ? 'bg-slate-500 text-white shadow-sm' 
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Archived
                </button>
              </div>
            </div>

            {/* Back Button */}
            {viewLevel !== 'mission' && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-slate-400 hover:text-white hover:bg-slate-900/50 text-xs sm:text-sm"
                onClick={() => {
                    setIsExiting(true);
                    setTimeout(() => {
                        let targetLevel = 'mission';
                        let targetParentId = null;
                        if (viewLevel === 'activity') {
                            targetLevel = 'objective';
                            const currentObj = objectives.find(o => o.id === parentId);
                            targetParentId = currentObj ? currentObj.mission_id : null;
                        } else if (viewLevel === 'objective') {
                            targetLevel = 'mission';
                            targetParentId = null;
                        }
                        const containerW = mapRef.current?.clientWidth || window.innerWidth;
                        const containerH = mapRef.current?.clientHeight || window.innerHeight;
                        let targetList = [];
                        if (targetLevel === 'mission') targetList = missions;
                        else if (targetLevel === 'objective') targetList = objectives.filter(o => o.mission_id === targetParentId);
                        const focusNodeId = parentId; 
                        const focusIndex = targetList.findIndex(item => item.id === focusNodeId);
                        let startPanX = containerW / 2;
                        let startPanY = containerH / 2;
                        if (focusIndex !== -1) {
                            const layout = generateHexLayout(targetList);
                            const node = layout[focusIndex];
                            if (node) {
                                const { x, y } = hexToPixel(node.q, node.r, HEX_SIZE, node.isOddRow);
                                const startZoom = 15;
                                startPanX = (containerW / 2) - (x * startZoom);
                                startPanY = (containerH / 2) - (y * startZoom);
                            }
                        }
                        setViewLevel(targetLevel);
                        setParentId(targetParentId);
                        setBreadcrumbs(prev => {
                            if (prev.length > 0) {
                                const newCrumbs = [...prev];
                                newCrumbs.pop();
                                return newCrumbs;
                            }
                            return [];
                        });
                        setIsExiting(false);
                        setTransitionDuration(0);
                        setZoom(15);
                        setPan({ x: startPanX, y: startPanY });
                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                setTransitionDuration(1.2);
                                setZoom(1);
                                setPan({ x: containerW / 2, y: containerH / 2 });
                            }, 50);
                        });
                    }, 400); 
                }}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Back to {viewLevel === 'activity' ? 'Objectives' : 'Missions'}
              </Button>
            )}
          </div>
          
          {/* SVG Layer — also handles pan/zoom/drag events */}
          <svg 
              ref={mapRef}
              className="w-full h-full absolute inset-0 cursor-move touch-none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={(e) => {
                if (e.touches.length === 1) {
                  setTransitionDuration(0);
                  setIsDragging(true);
                  setLastMousePos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
                  setActiveHexId(null);
                }
              }}
              onTouchMove={(e) => {
                if (e.touches.length === 1 && isDragging && !draggingHex) {
                  setTransitionDuration(0);
                  const dx = e.touches[0].clientX - lastMousePos.x;
                  const dy = e.touches[0].clientY - lastMousePos.y;
                  setPan(p => ({ x: p.x + dx, y: p.y + dy }));
                  setLastMousePos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
                } else if (e.touches.length === 2) {
                  const touch1 = e.touches[0];
                  const touch2 = e.touches[1];
                  const dist = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
                  if (lastPinchDist.current) {
                    const delta = dist - lastPinchDist.current;
                    setZoom(z => Math.min(Math.max(0.2, z + delta * 0.005), 3));
                  }
                  lastPinchDist.current = dist;
                }
              }}
              onTouchEnd={() => {
                setIsDragging(false);
                lastPinchDist.current = null;
              }}
          >
              <g 
                transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}
                style={{ transition: transitionDuration > 0 ? `transform ${transitionDuration}s ease-in-out` : 'none' }}
              >
                  <defs>
                      <pattern id="hexGrid" width="60" height="60" patternUnits="userSpaceOnUse" patternTransform="scale(2)">
                        <path d="M30 0l25.98 15v30L30 60 4.02 45V15z" fill="none" stroke="#1e293b" strokeWidth="1" opacity="0.5"/>
                      </pattern>
                  </defs>
                  
                  {/* Background Grid - Large enough to cover panning */}
                  <rect x={-50000} y={-50000} width={100000} height={100000} fill="url(#hexGrid)" opacity="0.2" />

                  {/* Cluster Labels (for By Owner mode) */}
                  <AnimatePresence>
                    {groupingMode === 'by_owner' && clusterLabels.map((label) => {
                      const initials = label.callsign?.slice(0, 2).toUpperCase() || '??';
                      return (
                        <motion.g
                          key={label.id}
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.5 }}
                          transition={{ delay: 0.6, duration: 0.6 }}
                        >
                          <foreignObject x={label.x - 50} y={label.y - 25} width={100} height={60}>
                            <div className="flex items-center justify-center gap-1">
                              {label.avatar ? (
                                <img 
                                  src={label.avatar} 
                                  alt={label.callsign}
                                  className="w-10 h-10 rounded-full border-2 border-indigo-500 shadow-lg"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full border-2 border-indigo-500 bg-indigo-600 flex items-center justify-center shadow-lg">
                                  <span className="text-xs font-bold text-white">{initials}</span>
                                </div>
                              )}
                              <div className="w-10 h-10 rounded-full border-2 border-indigo-400/50 bg-indigo-600/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                                <span className="text-xs font-bold text-white">{initials}</span>
                              </div>
                            </div>
                          </foreignObject>
                        </motion.g>
                      );
                    })}
                  </AnimatePresence>

                  {/* Hex Nodes */}
                  {hexLayout.map((node, index) => {
                      // Calculate position based on grouping mode
                      let x, y;
                      if (node.customX !== undefined && node.customY !== undefined) {
                        x = node.customX;
                        y = node.customY;
                      } else {
                        const basePos = hexToPixel(node.q, node.r, HEX_SIZE, node.isOddRow);
                        x = basePos.x + (node.clusterOffsetX || 0);
                        y = basePos.y + (node.clusterOffsetY || 0);
                      } 
                      const isSelected = selectedNode?.id === node.item.id;
                      const isActive = activeHexId === node.item.id;
                      const statusColorClass = getHexStyles(node.item.visualStatus);
                      
                      const handleDrillDown = () => {
                          setTransitionDuration(0.8);
                          const containerW = mapRef.current?.clientWidth || window.innerWidth;
                          const containerH = mapRef.current?.clientHeight || window.innerHeight;
                          
                          // Target: Center the hex and zoom deep (15x to simulate entering)
                          const targetZoom = 15; 
                          const targetPanX = (containerW / 2) - (x * targetZoom);
                          const targetPanY = (containerH / 2) - (y * targetZoom);
                          
                          setZoom(targetZoom);
                          setPan({ x: targetPanX, y: targetPanY });

                          setTimeout(() => {
                              setBreadcrumbs(prev => [...prev, { level: viewLevel, id: parentId, title: viewLevel === 'mission' ? 'Missions' : selectedNode?.title || 'Unknown' }]);
                              setParentId(node.item.id);
                              setViewLevel(node.item.nodeType === 'mission' ? 'objective' : 'activity');
                              setSelectedNode(null);
                              setActiveHexId(null);
                              
                              // Reset view instantly for new level (items will animate in via motion.g)
                              setTransitionDuration(0);
                              setZoom(1); 
                              setPan({ x: containerW / 2, y: containerH / 2 });
                          }, 800);
                      };

                      const handleShowDetails = () => {
                          if (node.item.nodeType === 'activity') {
                              setSelectedRTSActivity(node.item);
                          } else {
                              setSelectedNode(node.item);
                              setIsMobileRightSidebarOpen(true);
                          }
                          setActiveHexId(null); // Close controls after selecting
                      };

                      const getButtonStyles = (status) => {
                      switch(status) {
                          case 'active': return 'bg-transparent border-emerald-500 text-emerald-500 hover:bg-emerald-500/20';
                          case 'critical': return 'bg-transparent border-rose-500 text-rose-500 hover:bg-rose-500/20';
                          case 'contested': return 'bg-transparent border-amber-500 text-amber-500 hover:bg-amber-500/20';
                          case 'scout': return 'bg-transparent border-cyan-500 text-cyan-500 hover:bg-cyan-500/20';
                          case 'conquered': return 'bg-transparent border-yellow-400 text-yellow-400 hover:bg-yellow-400/20';
                          case 'abandoned': return 'bg-transparent border-slate-500 text-slate-500 hover:bg-slate-500/20';
                          default: return 'bg-transparent border-indigo-500 text-indigo-500 hover:bg-indigo-500/20';
                      }
                      };

                      const buttonClass = getButtonStyles(node.item.visualStatus);

                      const handleHexMouseDown = (e) => {
                        // Only allow drag in custom mode
                        if (groupingMode !== 'custom') return;
                        
                        e.stopPropagation();
                        const rect = mapRef.current.getBoundingClientRect();
                        const mouseX = (e.clientX - rect.left - pan.x) / zoom;
                        const mouseY = (e.clientY - rect.top - pan.y) / zoom;
                        setDragOffset({ x: mouseX - x, y: mouseY - y });
                        setDraggingHex(node.item.id);
                      };

                      return (
                      <motion.g 
                      key={node.item.id} 
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ 
                        scale: isExiting ? 0 : 1, 
                        opacity: isExiting ? 0 : 1,
                        x: isGroupTransitioning ? [0, -4, 4, -4, 4, -3, 3, -2, 2, 0] : 0,
                        y: isGroupTransitioning ? [0, 4, -4, 4, -4, 3, -3, 2, -2, 0] : 0,
                      }}
                      transition={{ 
                          duration: isGroupTransitioning ? 0.8 : 0.8, 
                          delay: isExiting ? 0 : 0.03 * Math.min(index, 15), 
                          ease: "easeInOut",
                          x: { duration: 0.8, ease: "easeInOut" },
                          y: { duration: 0.8, ease: "easeInOut" },
                      }}
                      style={{ transformOrigin: `${x}px ${y}px` }}
                      className={`group ${groupingMode === 'custom' ? (draggingHex === node.item.id ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-pointer'}`}
                          onMouseDown={handleHexMouseDown}
                          onClick={(e) => {
                                  e.stopPropagation();
                                  if (draggingHex) return; // Don't trigger click if we were dragging
                                  if (node.item.nodeType === 'activity') {
                                      handleShowDetails();
                                  } else {
                                      setActiveHexId(node.item.id);
                                      // Center camera on click? Optional but nice
                                      setTransitionDuration(0.3);
                                  }
                              }}
                          >
                              <HexagonShape
                                  x={x}
                                  y={y}
                                  size={HEX_SIZE - 5}
                                  selected={isSelected || isActive}
                                  className={statusColorClass}
                                  onClick={() => {}} 
                              >
                                  {/* Content inside Hex */}
                                  <div className={`flex flex-col items-center justify-center pointer-events-none w-full h-full px-1 transition-opacity duration-300 ${isActive ? 'opacity-20 blur-[1px]' : 'opacity-100'}`}>
                                      {/* Code */}
                                      <div className="text-[10px] font-mono text-slate-400 mb-0.5 tracking-wider font-bold">
                                          {node.item.code || '---'}
                                      </div>

                                      {/* Title */}
                                      <div className={`text-[9px] uppercase font-bold text-center leading-tight max-w-full line-clamp-2 mb-2 ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                                          {node.item.title}
                                      </div>
                                      
                                      {/* Nested Stats Lines */}
                                      <div className="w-full space-y-1">
                                          {/* Line 1 */}
                                          {node.item.nodeType === 'mission' && (
                                              <div className="flex items-center gap-1 w-full">
                                                  <Compass size={8} className={`${statusColorClass.includes('rose') ? 'text-rose-400' : statusColorClass.includes('emerald') ? 'text-emerald-400' : 'text-amber-400'} shrink-0`} />
                                                  <span className="text-[7px] text-slate-400 w-3">{node.item.stats.objectives.total}</span>
                                                  <div className="h-[1.5px] w-[60%] flex rounded-sm overflow-hidden bg-slate-800">
                                                      <div style={{width: `${(node.item.stats.objectives.green / node.item.stats.objectives.total) * 100}%`}} className="bg-emerald-500 h-full"/>
                                                      <div style={{width: `${(node.item.stats.objectives.yellow / node.item.stats.objectives.total) * 100}%`}} className="bg-amber-500 h-full"/>
                                                      <div style={{width: `${(node.item.stats.objectives.red / node.item.stats.objectives.total) * 100}%`}} className="bg-rose-500 h-full"/>
                                                  </div>
                                              </div>
                                          )}
                                          
                                          {/* Line 2 (Mission) or Line 1 (Objective) */}
                                          {(node.item.nodeType === 'mission' || node.item.nodeType === 'objective') && (
                                              <div className="flex items-center gap-1 w-full">
                                                  <Activity size={8} className={`${statusColorClass.includes('rose') ? 'text-rose-400' : statusColorClass.includes('emerald') ? 'text-emerald-400' : 'text-amber-400'} shrink-0`} />
                                                  <span className="text-[7px] text-slate-400 w-3">{node.item.stats.activities.total}</span>
                                                  <div className="h-[1.5px] w-[60%] flex rounded-sm overflow-hidden bg-slate-800">
                                                      <div style={{width: `${(node.item.stats.activities.green / node.item.stats.activities.total) * 100}%`}} className="bg-emerald-500 h-full"/>
                                                      <div style={{width: `${(node.item.stats.activities.yellow / node.item.stats.activities.total) * 100}%`}} className="bg-amber-500 h-full"/>
                                                      <div style={{width: `${(node.item.stats.activities.red / node.item.stats.activities.total) * 100}%`}} className="bg-rose-500 h-full"/>
                                                  </div>
                                              </div>
                                          )}

                                          {/* Line 3 (Mission), Line 2 (Objective), Line 1 (Activity) */}
                                          <div className="flex items-center gap-1 w-full">
                                              <CheckSquare size={8} className={`${statusColorClass.includes('rose') ? 'text-rose-400' : statusColorClass.includes('emerald') ? 'text-emerald-400' : 'text-amber-400'} shrink-0`} />
                                              <span className="text-[7px] text-slate-400 w-3">{node.item.stats.tasks.total}</span>
                                              <div className="h-[1.5px] w-[60%] flex rounded-sm overflow-hidden bg-slate-800">
                                                  <div style={{width: `${(node.item.stats.tasks.green / node.item.stats.tasks.total) * 100}%`}} className="bg-emerald-500 h-full"/>
                                                  <div style={{width: `${(node.item.stats.tasks.yellow / node.item.stats.tasks.total) * 100}%`}} className="bg-amber-500 h-full"/>
                                                  <div style={{width: `${(node.item.stats.tasks.red / node.item.stats.tasks.total) * 100}%`}} className="bg-rose-500 h-full"/>
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                              </HexagonShape>

                              {/* Controls Overlay (Zoom & Info) */}
                              <AnimatePresence>
                                  {isActive && (
                                      <foreignObject x={x - HEX_SIZE} y={y - HEX_SIZE} width={HEX_SIZE * 2} height={HEX_SIZE * 2} className="pointer-events-auto overflow-visible">
                                          <motion.div 
                                              initial={{ opacity: 0, scale: 0.8 }}
                                              animate={{ opacity: 1, scale: 1 }}
                                              exit={{ opacity: 0, scale: 0.8 }}
                                              className="w-full h-full flex items-center justify-center gap-4"
                                              onMouseDown={(e) => e.stopPropagation()}
                                          >
                                              {/* Zoom In Button (Drill Down) - Hide for activities */}
                                              {node.item.nodeType !== 'activity' && (
                                                  <button 
                                                      onMouseDown={(e) => e.stopPropagation()}
                                                      onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleDrillDown();
                                                      }}
                                                      className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 hover:scale-110 transition-transform ${buttonClass}`}
                                                      title="Drill Down / Zoom In"
                                                  >
                                                      <ZoomIn className="w-5 h-5" />
                                                  </button>
                                              )}
                                              
                                              {/* Info Button (Details) */}
                                              <button 
                                                  onMouseDown={(e) => e.stopPropagation()}
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleShowDetails();
                                                  }}
                                                  className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 hover:scale-110 transition-transform ${buttonClass}`}
                                                  title="View Details"
                                              >
                                                  <Info className="w-5 h-5" />
                                              </button>
                                          </motion.div>
                                      </foreignObject>
                                  )}
                              </AnimatePresence>
                          </motion.g>
                      );
                  })}
              </g>
          </svg>
          
          {/* Back to Command Deck Button (Top Left) */}
          <div className="absolute top-4 left-4 z-10">
              <Link to={createPageUrl('')}>
                <Button size="icon" variant="secondary" className="bg-[#0B1221]/90 border-2 border-amber-500/50 shadow-lg w-14 h-14 hover:bg-indigo-600 hover:border-indigo-500 transition-colors" title="Back to Command Deck">
                    <Gamepad2 className="w-8 h-8 text-amber-400" />
                </Button>
              </Link>
          </div>

          {/* Controls Overlay (Bottom Right) */}
          <div className="absolute bottom-4 sm:bottom-8 right-4 sm:right-8 flex flex-col gap-1.5 sm:gap-2 z-10 pointer-events-auto">
              <div className="hidden md:block bg-slate-900/80 backdrop-blur p-2 rounded-lg border border-slate-800 text-slate-400 text-xs mb-2">
                  <div className="flex items-center gap-2"><Move className="w-3 h-3" /> Drag to Pan</div>
                  <div className="flex items-center gap-2"><ZoomIn className="w-3 h-3" /> Scroll to Zoom</div>
              </div>
              <Button size="icon" variant="secondary" onClick={() => setZoom(z => Math.min(z + 0.2, 3))} className="w-9 h-9 sm:w-10 sm:h-10">
                  <ZoomIn className="w-4 h-4" />
              </Button>
              <Button size="icon" variant="secondary" onClick={() => setZoom(z => Math.max(z - 0.2, 0.2))} className="w-9 h-9 sm:w-10 sm:h-10">
                  <ZoomOut className="w-4 h-4" />
              </Button>
              <Button size="icon" variant="secondary" onClick={fitBounds} title="Fit to Screen" className="w-9 h-9 sm:w-10 sm:h-10">
                  <Maximize className="w-4 h-4" />
              </Button>
          </div>
        </main>


        {/* --- RIGHT SIDEBAR --- */}
        <div className="hidden lg:block relative z-20">
          {/* Collapse Toggle Button - Always visible */}
          <button
            onClick={() => setIsRightSidebarCollapsed(!isRightSidebarCollapsed)}
            className="absolute top-4 left-0 transform -translate-x-full z-40 bg-indigo-600 hover:bg-indigo-500 border-2 border-indigo-400 rounded-l-lg px-1.5 py-3 shadow-lg transition-colors"
          >
            {isRightSidebarCollapsed ? (
              <ChevronLeft className="w-5 h-5 text-white" />
            ) : (
              <ChevronRight className="w-5 h-5 text-white" />
            )}
          </button>
          
          <AnimatePresence initial={false}>
            <motion.aside 
              initial={false}
              animate={{ width: isRightSidebarCollapsed ? 0 : 320 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="h-full bg-[#0B1221] border-l border-slate-800 flex flex-col shadow-xl overflow-hidden"
            >
              <div className="w-80 h-full flex flex-col">
                <RightSidebarContent 
                node={selectedNode} 
                viewLevel={viewLevel}
                currentItems={currentItems}
                onNodeSelect={(nodeId) => {
                  const foundNode = currentItems.find(item => item.id === nodeId);
                  if (foundNode) {
                    if (foundNode.nodeType === 'activity') {
                      setSelectedRTSActivity(foundNode);
                    } else {
                      setSelectedNode(foundNode);
                      setActiveHexId(nodeId);
                    }
                  }
                }}
                onDrillToObjective={handleDrillToObjective}
                onDrillToActivity={handleDrillToActivity}
                onOpenActivity={(activity) => setSelectedRTSActivity(activity)}
                objectives={objectives}
                activities={activities}
                missions={missions}
                officers={officers}
              />
              </div>
            </motion.aside>
          </AnimatePresence>
        </div>

        {/* Mobile Right Sidebar */}
        <AnimatePresence>
          {isMobileRightSidebarOpen && (
            <>
              <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsMobileRightSidebarOpen(false)}
                  className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
              />
              <motion.aside
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="fixed inset-y-0 right-0 w-[85%] max-w-[320px] bg-[#0B1221] border-l border-slate-800 z-50 lg:hidden shadow-2xl flex flex-col"
              >
                  <div className="absolute top-2 right-full mr-2">
                      <Button size="icon" variant="destructive" onClick={() => setIsMobileRightSidebarOpen(false)} className="rounded-full w-8 h-8 shadow-lg">
                          <X className="w-4 h-4" />
                      </Button>
                  </div>
                  <RightSidebarContent 
                    node={selectedNode}
                    viewLevel={viewLevel}
                    currentItems={currentItems}
                    onNodeSelect={(nodeId) => {
                      const foundNode = currentItems.find(item => item.id === nodeId);
                      if (foundNode) {
                        if (foundNode.nodeType === 'activity') {
                          setSelectedRTSActivity(foundNode);
                        } else {
                          setSelectedNode(foundNode);
                          setActiveHexId(nodeId);
                        }
                        setIsMobileRightSidebarOpen(false);
                      }
                    }}
                    onDrillToObjective={handleDrillToObjective}
                    onDrillToActivity={handleDrillToActivity}
                    onOpenActivity={(activity) => setSelectedRTSActivity(activity)}
                    objectives={objectives}
                    activities={activities}
                    missions={missions}
                    officers={officers}
                      />
                    </motion.aside>
                    </>
                    )}
                    </AnimatePresence>
      </div>
    </div>
  );
}



function RightSidebarContent({ node, viewLevel, currentItems, onNodeSelect, onDrillToObjective, onDrillToActivity, onOpenActivity, objectives, activities, missions, officers }) {
    const navigate = useNavigate();

    const getLink = () => {
        if (!node) return '#';
        if (node.nodeType === 'mission') return createPageUrl('missions') + `?missionId=${node.id}`;
        if (node.nodeType === 'objective') return createPageUrl('discovery') + `?objectiveId=${node.id}`;
        if (node.nodeType === 'activity') return createPageUrl('activities') + `?activityId=${node.id}`;
        return '#';
    };

    return (
        <>
            <div className="p-4 border-b border-slate-800/60 bg-[#0B1221] shrink-0">
                <h2 className="text-sm font-bold text-slate-100 uppercase tracking-widest flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-indigo-400" />
                    {node ? `${node.nodeType} Details` : "Select a Hex"}
                </h2>
                <NodeSelector
                  viewLevel={viewLevel}
                  items={currentItems}
                  onSelect={onNodeSelect}
                  onDrillToObjective={onDrillToObjective}
                  onDrillToActivity={onDrillToActivity}
                  objectives={objectives}
                  activities={activities}
                  missions={missions}
                />
            </div>
            
            <ScrollArea className="flex-1">
                <div className="p-6">
                    {node ? (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            {/* Key Officers */}
                            {node.officers && node.officers.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Key Officers</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {node.officers.map(email => {
                                            const officer = officers.find(o => o.user_email === email);
                                            const initials = (officer?.callsign || email?.split('@')[0] || '??').slice(0, 2).toUpperCase();
                                            return (
                                                <div key={email} className="flex items-center gap-2 p-2 bg-slate-900 rounded border border-slate-800">
                                                    {officer?.avatar_url ? (
                                                        <img src={officer.avatar_url} alt={officer.callsign} className="w-6 h-6 rounded-full border border-indigo-500" />
                                                    ) : (
                                                        <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center border border-indigo-500">
                                                            <span className="text-[10px] font-bold text-white">{initials}</span>
                                                        </div>
                                                    )}
                                                    <span className="text-sm text-slate-300">{officer?.callsign || email?.split('@')[0]}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div>
                                <h3 className="text-xl font-bold text-amber-400 font-serif mb-1">{node.title}</h3>
                                <div className="text-sm font-semibold text-amber-400/80 mb-2">
                                    {node.xp_reward || 0} XP
                                </div>

                                {/* Stats Breakdown Bars */}
                                {node.stats && (
                                    <div className="space-y-2 mt-4 mb-2">
                                        {node.stats.objectives && (
                                            <div className="space-y-1">
                                                <div className="flex justify-between text-[10px] text-slate-500 uppercase tracking-wider">
                                                    <span>Objectives</span>
                                                    <span>{node.stats.objectives.total}</span>
                                                </div>
                                                <div className="h-1.5 w-full flex rounded-full overflow-hidden bg-slate-800">
                                                    <div style={{width: `${(node.stats.objectives.green / node.stats.objectives.total) * 100}%`}} className="bg-emerald-500 h-full"/>
                                                    <div style={{width: `${(node.stats.objectives.yellow / node.stats.objectives.total) * 100}%`}} className="bg-amber-500 h-full"/>
                                                    <div style={{width: `${(node.stats.objectives.red / node.stats.objectives.total) * 100}%`}} className="bg-rose-500 h-full"/>
                                                </div>
                                            </div>
                                        )}
                                        {node.stats.activities && (
                                            <div className="space-y-1">
                                                <div className="flex justify-between text-[10px] text-slate-500 uppercase tracking-wider">
                                                    <span>Activities</span>
                                                    <span>{node.stats.activities.total}</span>
                                                </div>
                                                <div className="h-1.5 w-full flex rounded-full overflow-hidden bg-slate-800">
                                                    <div style={{width: `${(node.stats.activities.green / node.stats.activities.total) * 100}%`}} className="bg-emerald-500 h-full"/>
                                                    <div style={{width: `${(node.stats.activities.yellow / node.stats.activities.total) * 100}%`}} className="bg-amber-500 h-full"/>
                                                    <div style={{width: `${(node.stats.activities.red / node.stats.activities.total) * 100}%`}} className="bg-rose-500 h-full"/>
                                                </div>
                                            </div>
                                        )}
                                        {node.stats.tasks && (
                                            <div className="space-y-1">
                                                <div className="flex justify-between text-[10px] text-slate-500 uppercase tracking-wider">
                                                    <span>Tasks</span>
                                                    <span>{node.stats.tasks.total}</span>
                                                </div>
                                                <div className="h-1.5 w-full flex rounded-full overflow-hidden bg-slate-800">
                                                    <div style={{width: `${(node.stats.tasks.green / node.stats.tasks.total) * 100}%`}} className="bg-emerald-500 h-full"/>
                                                    <div style={{width: `${(node.stats.tasks.yellow / node.stats.tasks.total) * 100}%`}} className="bg-amber-500 h-full"/>
                                                    <div style={{width: `${(node.stats.tasks.red / node.stats.tasks.total) * 100}%`}} className="bg-rose-500 h-full"/>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <Separator className="bg-slate-800" />
                            
                            <div className="space-y-3">
                                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Description</h4>
                                <p className="text-sm text-slate-300 leading-relaxed">
                                    {node.description || node.statement || node.objective || "No description available."}
                                </p>
                            </div>

                            {node.nodeType === 'mission' && (
                                 <div className="space-y-3">
                                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Commander's Intent</h4>
                                    <p className="text-sm text-slate-400 leading-relaxed italic">
                                        {node.commanders_intent || "Classified."}
                                    </p>
                                </div>
                            )}
                            
                             {node.nodeType !== 'activity' && (
                                <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg text-xs text-indigo-300">
                                    <div className="font-bold mb-1 flex items-center gap-2">
                                        <ZoomIn className="w-3 h-3" />
                                        Interactive Map
                                    </div>
                                    Click the hex again to drill down into {node.childCount} {node.nodeType === 'mission' ? 'objectives' : 'activities'}.
                                </div>
                             )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 opacity-50">
                            <div className="w-16 h-16 rounded-full bg-slate-800/50 flex items-center justify-center border border-slate-700 border-dashed">
                                <Hexagon className="w-8 h-8 text-slate-500" />
                            </div>
                            <div className="space-y-1">
                                <p className="text-slate-300 font-medium">Awaiting Selection</p>
                                <p className="text-xs text-slate-500 max-w-[200px] mx-auto">Select a node to view details or click again to zoom in.</p>
                            </div>
                        </div>
                    )}
                </div>
            </ScrollArea>
            
            {/* Sidebar Footer */}
            {node && (
                <div className="p-4 border-t border-slate-800 bg-[#080E1A] shrink-0">
                    <Button 
                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
                        onClick={() => navigate(getLink())}
                    >
                        View Full Details
                    </Button>
                </div>
            )}
        </>
    );
}